import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export interface SlashCommand {
  name: string
  description: string
}

interface SlashCommandMenuProps {
  commands: SlashCommand[]
  filter: string
  onSelect: (command: SlashCommand) => void
  activeIndex: number
  onActiveIndexChange: (index: number) => void
  /** Bounding rect of the input container, used for fixed positioning via portal */
  anchor: { top: number; left: number; width: number }
}

export function SlashCommandMenu({
  commands,
  filter,
  onSelect,
  activeIndex,
  onActiveIndexChange,
  anchor,
}: SlashCommandMenuProps) {
  const activeItemRef = useRef<HTMLButtonElement>(null)

  // Filter commands by prefix match (case-insensitive)
  const filteredCommands = commands.filter((cmd) =>
    cmd.name.toLowerCase().startsWith(filter.toLowerCase())
  )

  // Clamp active index to filtered list
  useEffect(() => {
    if (activeIndex >= filteredCommands.length) {
      onActiveIndexChange(Math.max(0, filteredCommands.length - 1))
    }
  }, [filteredCommands.length, activeIndex, onActiveIndexChange])

  // Scroll active item into view
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [activeIndex])

  if (filteredCommands.length === 0) {
    return null
  }

  // Menu sits just above the input container, anchored to its left edge
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: `calc(100vh - ${anchor.top}px + 4px)`,
    left: anchor.left,
    width: Math.min(anchor.width, 480),
    zIndex: 9999,
  }

  return createPortal(
    <div
      style={menuStyle}
      className="bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl overflow-hidden"
    >
      <div className="max-h-[16rem] overflow-y-auto">
        {filteredCommands.map((command, index) => (
          <button
            key={command.name}
            ref={index === activeIndex ? activeItemRef : null}
            type="button"
            onClick={() => onSelect(command)}
            className={`w-full text-left px-4 py-2.5 transition-colors border-b border-white/5 last:border-b-0 ${
              index === activeIndex
                ? 'bg-white/5'
                : 'hover:bg-white/5'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-violet-400 font-medium text-sm flex-shrink-0">
                /{command.name}
              </span>
              <span className="text-white/40 text-xs leading-relaxed">
                {command.description}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>,
    document.body
  )
}
